var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { Box, MenuItem, Select, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { fetchDashboardFilterGroup } from "../../services/api/dashboard";
import { DashboardClearFiltersButton } from "./DashboardClearFiltersButton";
var ALL_OPTION = "__all__";
var FILTER_OPTION_DEPENDENCY_KEYS = ["periodo", "territorio"];
function buildFilterDependencyKey(values) {
    return FILTER_OPTION_DEPENDENCY_KEYS.reduce(function (acc, key) {
        var value = values[key];
        if (Array.isArray(value) && value.length > 0) {
            acc[key] = value;
        }
        if (typeof value === "string" && value) {
            acc[key] = value;
        }
        return acc;
    }, {});
}
export function DashboardFiltersSkeleton(_a) {
    var _b = _a.count, count = _b === void 0 ? 2 : _b, _c = _a.labels, labels = _c === void 0 ? [] : _c;
    return (_jsx(Box, __assign({ sx: { display: "flex", flexWrap: "wrap", gap: 2, mb: 3 } }, { children: Array.from({ length: count }, function (_, index) {
            var _a;
            return (_jsxs(Box, __assign({ sx: { minWidth: 180 } }, { children: [_jsx(Typography, __assign({ sx: {
                            fontFamily: "Montserrat, sans-serif",
                            fontSize: "0.75rem",
                            color: "#6B7280",
                            lineHeight: 1,
                            mb: 0.5,
                            ml: 1,
                        } }, { children: (_a = labels[index]) !== null && _a !== void 0 ? _a : "Filtro" })), _jsx(Select, __assign({ disabled: true, size: "small", value: "", displayEmpty: true, renderValue: function () { return "Carregando..."; }, sx: {
                            borderRadius: "10px",
                            fontFamily: "Montserrat, sans-serif",
                            fontSize: "0.85rem",
                            color: "#6B7280",
                            fontWeight: 600,
                            minWidth: 180,
                            bgcolor: "#F9FAFB",
                            "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#E5E7EB",
                            },
                        } }, { children: _jsx(MenuItem, __assign({ value: "" }, { children: "Carregando..." })) }))] }), index));
        }) })));
}
var normalizeValue = function (value) {
    return Array.isArray(value) ? value : value ? value.split(",").filter(Boolean) : [];
};
var renderSelectedValue = function (selected, options, emptyLabel) {
    var selectedValues = normalizeValue(selected).filter(function (item) { return item !== ALL_OPTION; });
    if (selectedValues.length === 0)
        return emptyLabel;
    var labelsByValue = new Map(options.map(function (item) { return [item.value, item.label]; }));
    return selectedValues
        .map(function (item) { var _a; return (_a = labelsByValue.get(item)) !== null && _a !== void 0 ? _a : item; })
        .join(", ");
};
var hasActiveFilterValues = function (values) {
    return Object.values(values).some(function (value) {
        return Array.isArray(value) ? value.length > 0 : Boolean(value);
    });
};
export function DashboardFilters(_a) {
    var _b, _c;
    var endpoint = _a.endpoint, values = _a.values, onChange = _a.onChange, onClear = _a.onClear, _d = _a.testIdPrefix, testIdPrefix = _d === void 0 ? "dashboard-filter" : _d;
    var dependencyKey = buildFilterDependencyKey(values);
    var filtersQuery = useQuery({
        queryKey: ["dashboard", "filter-group", endpoint, dependencyKey],
        queryFn: function () { return fetchDashboardFilterGroup({ endpoint: endpoint, filters: values }); },
        staleTime: 5 * 60 * 1000,
    });
    var definitions = (_c = (_b = filtersQuery.data) === null || _b === void 0 ? void 0 : _b.items) !== null && _c !== void 0 ? _c : [];
    var loading = filtersQuery.isLoading;
    var filtersActive = hasActiveFilterValues(values);
    if (loading && definitions.length === 0) {
        return _jsx(DashboardFiltersSkeleton, { count: 2 });
    }
    return (_jsxs(Box, __assign({ sx: {
            display: "flex",
            flexWrap: "wrap",
            alignItems: "flex-end",
            gap: 2,
            mb: 3,
        } }, { children: [definitions.map(function (state) {
                var _a;
                var value = (_a = values[state.id]) !== null && _a !== void 0 ? _a : (state.multiple ? [] : "");
                var emptyLabel = state.id === "periodo"
                    ? "Todos os anos"
                    : state.id === "mes"
                        ? "Todos os meses"
                        : "Todos";
                return (_jsxs(Box, __assign({ sx: { minWidth: 180 } }, { children: [_jsx(Typography, __assign({ sx: {
                                fontFamily: "Montserrat, sans-serif",
                                fontSize: "0.75rem",
                                color: "#6B7280",
                                lineHeight: 1,
                                mb: 0.5,
                                ml: 1,
                            } }, { children: state.label })), loading ? (_jsx(Select, __assign({ "data-testid": "".concat(testIdPrefix, "-").concat(state.id, "-loading"), disabled: true, size: "small", value: "", displayEmpty: true, renderValue: function () { return "Carregando..."; }, sx: {
                                borderRadius: "10px",
                                fontFamily: "Montserrat, sans-serif",
                                fontSize: "0.85rem",
                                color: "#6B7280",
                                fontWeight: 600,
                                minWidth: 180,
                                bgcolor: "#F9FAFB",
                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#E5E7EB",
                                },
                            } }, { children: _jsx(MenuItem, __assign({ value: "" }, { children: "Carregando..." })) }))) : (_jsxs(Select, __assign({ multiple: state.multiple, displayEmpty: true, disabled: !state.enabled, size: "small", "data-testid": "".concat(testIdPrefix, "-").concat(state.id), value: value, renderValue: function (selected) {
                                return renderSelectedValue(selected, state.options, emptyLabel);
                            }, onChange: function (event) {
                                var selectedValue = event.target.value;
                                if (state.multiple) {
                                    var selectedValues = normalizeValue(selectedValue);
                                    onChange(state.id, selectedValues.includes(ALL_OPTION) ? [] : selectedValues);
                                    return;
                                }
                                onChange(state.id, selectedValue === ALL_OPTION ? "" : String(selectedValue));
                            }, sx: {
                                borderRadius: "10px",
                                fontFamily: "Montserrat, sans-serif",
                                fontSize: "0.85rem",
                                color: "#034EA2",
                                fontWeight: 600,
                                minWidth: 180,
                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "transparent",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#E5E7EB",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#034EA2",
                                },
                            } }, { children: [_jsx(MenuItem, __assign({ value: ALL_OPTION }, { children: emptyLabel })), state.options.map(function (option) { return (_jsx(MenuItem, __assign({ value: option.value }, { children: option.label }), option.value)); })] })))] }), state.id));
            }), onClear && (_jsx(Box, __assign({ sx: { minWidth: { xs: "100%", sm: "auto" } } }, { children: _jsx(DashboardClearFiltersButton, { onClick: onClear, disabled: !filtersActive, testId: "".concat(testIdPrefix, "-clear") }) })))] })));
}
